//===----------------------------------------------------------------------===//
//
//  Copyright 2024-2026 Trovares Inc. dba Rocketgraph.  All rights reserved.
//
//===----------------------------------------------------------------------===//

import {
  type RefObject, useEffect, useLayoutEffect, useRef, useState
} from 'react';

import { Box, Button, IconButton, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { visuallyHidden } from '@mui/utils';
import CloseIcon from '@mui/icons-material/Close';
import { TooltipRenderProps } from 'react-joyride';
import { useHelp } from '@/hooks/useHelp';
import { TourStep } from '@/components/Help/tourTypes';
import { landingStep } from './stepNavigation';
import CustomStepIndicator from './CustomStepIndicator';

// What names and describes Joyride's alertdialog, one pair per pane.
const TITLE_ID = 'tour-tooltip-title';
const CONTENT_ID = 'tour-tooltip-content';
const EXIT_TITLE_ID = 'tour-tooltip-exit-title';
const EXIT_BODY_ID = 'tour-tooltip-exit-body';

// The question the exit pane asks, shared with the status that announces
// it so the two cannot drift apart.
const EXIT_TITLE = 'Leave this tour?';
const EXIT_BODY = 'You can start any tour again from the Help menu.';

interface CustomTooltipProps extends Omit<TooltipRenderProps, 'step'> {
  step: TourStep;
  steps: TourStep[];
  // Whether leaving should ask first, which is worth doing only while the
  // tour can still come back uninvited. True however this run of it began,
  // since the next one can come uninvited even when the user picked this
  // one from the Help menu. False once it cannot: the tour never starts
  // itself, the user has finished it or asked not to see it again, or the
  // switch that stops anything opening itself is off.
  asksBeforeLeaving: boolean;
  // Where the tooltip publishes its answer for leaving, so the tour's own
  // Escape listener can give the key the same meaning the X has.
  leaveHandlerRef: RefObject<(() => void) | null>;
}

export const CustomTooltip = ({
  backProps,
  closeProps,
  continuous,
  index,
  primaryProps,
  step,
  tooltipProps,
  steps,
  asksBeforeLeaving,
  leaveHandlerRef
}: CustomTooltipProps) => {
  const theme = useTheme();
  const {
    tourActions: { setTourStep, endTour, finishTour },
    tourState: { isNextDisabled }
  } = useHelp();
  const [isConfirmingExit, setIsConfirmingExit] = useState(false);
  const leaveRef = useRef<HTMLButtonElement>(null);
  const firstChoiceRef = useRef<HTMLButtonElement>(null);
  const paneRef = useRef<HTMLDivElement>(null);
  const hasPrompted = useRef(false);

  // Whether the prompt still had the keyboard when it closed.  Remembered
  // from where attention last went rather than read back from
  // document.activeElement, which cannot tell a button of ours unmounting
  // from a click on a target that takes no focus -- a div, a list row, or
  // any button at all in Safari and Firefox.  All of those leave the
  // document holding focus.
  const paneHasFocus = useRef(false);

  // The control the keyboard was on when the question was raised, for Cancel
  // to hand it back to. Only one outside the pane is worth keeping: Escape
  // is answered wherever focus sits, which on a step that advances on a
  // click is a page control. The X, and the document that a press on a
  // button leaves focused in Safari and Firefox, are both answered by the X.
  const focusBeforePrompt = useRef<HTMLElement | null>(null);

  // Focus follows the pane every way it moves, so the keyboard is never left
  // on a button that has just unmounted -- including the way the user did
  // not ask for, a step change dismissing the prompt beneath their hands.
  useEffect(() => {
    if (isConfirmingExit) {
      hasPrompted.current = true;
      paneHasFocus.current = true;
      firstChoiceRef.current?.focus();
    } else if (hasPrompted.current && paneHasFocus.current) {
      const restore = focusBeforePrompt.current;
      if (restore?.isConnected) restore.focus();
      else leaveRef.current?.focus();
    }
  }, [isConfirmingExit]);

  // Where attention last went while the prompt is up: a press on the page,
  // which a spotlit target still accepts, or focus landing anywhere.  The
  // latest one wins rather than latching away, so a press out on the page
  // -- the overlay takes one and does nothing with it -- does not cost the
  // pane the keyboard it is given back a moment later, by a press on Cancel
  // or by Joyride focusing the tooltip.  Capture phase, because either can
  // be stopped before it bubbles.
  useEffect(() => {
    if (!isConfirmingExit) return;

    const noteAttention = (event: Event) => {
      const target = event.target;
      paneHasFocus.current =
        target instanceof Node && paneRef.current?.contains(target) === true;
    };
    document.addEventListener('pointerdown', noteAttention, true);
    document.addEventListener('focusin', noteAttention, true);
    return () => {
      document.removeEventListener('pointerdown', noteAttention, true);
      document.removeEventListener('focusin', noteAttention, true);
    };
  }, [isConfirmingExit]);

  // A step advancing beneath the prompt would otherwise leave a question
  // hanging over a step the user never saw.
  useEffect(() => { setIsConfirmingExit(false); }, [index]);

  const cancelExit = () => setIsConfirmingExit(false);

  // The X keeps its place and changes its job while the prompt is up, so
  // the name a screen reader announces has to change with it.
  const leaveLabel = isConfirmingExit ?
                     'Cancel leaving the tour' : 'Leave the tour';

  // The hint belongs to the step that is waiting, so it goes away with the
  // step pane rather than being left ruled under a question about leaving.
  const hint = !isConfirmingExit && isNextDisabled ?
               step.actionHint : undefined;

  // What the live region is carrying, always one commit behind the hint
  // itself.  Joyride mounts a fresh tooltip for every step and a step that
  // waits on the user holds Next from its beforeStep, which runs before
  // this renders at all -- so a region that read the hint as it rendered
  // would be inserted with its text already in it, and a region that
  // arrives carrying its text is not announced.  Filled from an effect
  // instead, so the region is in the document a commit before its text
  // whenever the hold was set.
  const [liveHint, setLiveHint] = useState<string | undefined>(undefined);
  useEffect(() => { setLiveHint(hint); }, [hint]);

  // The prompt replaces the dialog's heading and body in place while focus
  // stays inside the same dialog, and a screen reader re-announces a dialog
  // only when focus enters it -- so on its own the user would hear nothing
  // but the button that took focus. Filled one commit behind for the same
  // reason as the hint, and emptied on dismissal so the question is not
  // read again, out of context, on the region's next change.
  const [liveQuestion, setLiveQuestion] = useState('');
  useEffect(() => {
    setLiveQuestion(isConfirmingExit ? `${EXIT_TITLE} ${EXIT_BODY}` : '');
  }, [isConfirmingExit]);

  // One answer for every way out of the tooltip, so the X and Escape cannot
  // disagree: a prompt already up is dismissed rather than re-raised, which
  // would leave the X inert under the pane it opened.
  const handleLeave = () => {
    if (isConfirmingExit) {
      cancelExit();
    } else if (asksBeforeLeaving) {
      const active = document.activeElement;
      focusBeforePrompt.current =
        active instanceof HTMLElement && active !== document.body &&
        paneRef.current?.contains(active) === false ? active : null;
      setIsConfirmingExit(true);
    } else {
      endTour();
    }
  };

  // Published every render, so the tour's Escape listener always calls the
  // answer for the pane as it stands.  Escape is handled there rather than
  // here because a press lands on whatever has focus, which on a step that
  // advances on a click is a page element and not this tooltip at all.
  // A layout effect, not a passive one: the listener is a native handler
  // that does not wait for React to flush, so a press in the same frame as
  // a commit would otherwise run the previous render's answer.  Nothing
  // pins that choice -- under act() both kinds of effect have run by the
  // time a test can press anything -- so it rests on React's scheduling
  // contract rather than on a test.
  useLayoutEffect(() => {
    leaveHandlerRef.current = handleLeave;
    return () => { leaveHandlerRef.current = null; };
  });

  return (
    <Box {...tooltipProps} ref={paneRef}
      // Each branch names an id its own pane always renders, so neither
      // attribute can point at nothing: the exit pane's heading and body
      // are unconditional, and the step pane's content Box is too -- only
      // its title is optional, which is what the inner check is for.
      aria-labelledby={isConfirmingExit ? EXIT_TITLE_ID :
                       step.title ? TITLE_ID : undefined}
      aria-describedby={isConfirmingExit ? EXIT_BODY_ID : CONTENT_ID}
      sx={{
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        padding: 2,
        width: 320,
        borderRadius: '4px',
      }}
    >
      <Box sx={{ textAlign: 'center', position: 'relative', mb: '10px' }}>
        {(isConfirmingExit || step.title) && (
          <Typography
            variant="h6"
            id={isConfirmingExit ? EXIT_TITLE_ID : TITLE_ID}
            sx={{
              color: theme.colors.rocketRed.p50,
              fontWeight: theme.typography.fontWeightMedium,
              textAlign: 'center'
            }}
          >
            {isConfirmingExit ? EXIT_TITLE : step.title}
          </Typography>
        )}
        <IconButton
          // The overrides go after the spread, never before: Joyride's own
          // close() sets its action to CLOSE, which our STEP_AFTER handler
          // turns into a step change -- the X moving the tour on instead of
          // ending it.
          {...closeProps}
          ref={leaveRef}
          aria-label={leaveLabel}
          title={leaveLabel}
          onClick={handleLeave}
          sx={{
            borderRadius: theme.border.radius,
            padding: '5px',
            '& svg': { fontSize: '1.2rem' },
            position: 'absolute',
            right: '-8px',
            top: '-8px',
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>
      {isConfirmingExit ? (
        <>
          <Typography
            variant="body2"
            id={EXIT_BODY_ID}
            sx={{ color: 'text.secondary', mt: 1, mb: 2 }}
          >
            {EXIT_BODY}
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Button ref={firstChoiceRef} variant="contained" color="primary"
              onClick={endTour}>
              Leave, show it again later
            </Button>
            <Button variant="outlined" color="primary" onClick={finishTour}>
              Leave, don&apos;t show it again
            </Button>
            <Button onClick={cancelExit}>
              Cancel
            </Button>
          </Box>
        </>
      ) : (
        <>
          <CustomStepIndicator
            steps={steps}
            currentStep={index}
            // A dot lands where Next or Back would: past a step that said it
            // may be absent and is, in the direction of travel. Past either
            // end it is a jump to nowhere, and does nothing; a dot is not an
            // exit.
            goToStep={(target) => {
              const landing =
                landingStep(steps, target, target >= index ? 1 : -1);
              if (landing >= 0 && landing < steps.length) setTourStep(landing);
            }}
            disableNavigation={isNextDisabled}
          />
          <Box
            id={CONTENT_ID}
            sx={{
              color: theme.palette.text.primary,
              fontSize: theme.typography.body1.fontSize,
              lineHeight: theme.typography.body1.lineHeight,
              minHeight: '40px',
              whiteSpace: 'pre-line', // Support newlines in content.
              marginTop: '12px',
              marginBottom: '12px',
            }}
          >
            {step.content}
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Box sx={{ display: 'flex' }}>
              {index > 0 && (
                <Button color="primary" variant="contained" {...backProps}>
                  {backProps.title}
                </Button>
              )}
              {continuous && (
                <Button
                  color="primary" variant="contained" {...primaryProps}
                  // Not `disabled`: that takes the button out of the tab
                  // order, so a keyboard user holding at a step loses their
                  // place.
                  aria-disabled={isNextDisabled || undefined}
                  disableRipple={isNextDisabled}
                  onClick={(event) => {
                    if (isNextDisabled) return;
                    primaryProps.onClick(event);
                  }}
                  sx={{
                    marginLeft: 1,
                    ...(isNextDisabled ? {
                      cursor: 'default',
                      backgroundColor: theme.colors.grays.astral4,
                      color: theme.colors.grays.nearBlack,
                      // The theme's contained-primary hover would otherwise
                      // put near-black on rocketRed.p60, about 3.1:1.  jsdom
                      // resolves no :hover, so this half is unpinnable.
                      '&:hover': {
                        backgroundColor: theme.colors.grays.astral4
                      }
                    } : {})
                  }}
                >
                  {primaryProps.title}
                </Button>
              )}
            </Box>
          </Box>
        </>
      )}
      {/* Mounted whether or not there is a hint, and never keyed off
          anything that changes with the step: a live region that arrives
          together with its text is not announced. */}
      <Box
        aria-live="polite"
        sx={{
          mt: liveHint ? 2 : 0,
          pt: liveHint ? 1 : 0,
          borderTop: liveHint ?
                     `1px solid ${alpha(theme.palette.divider, 0.5)}` : 'none',
          textAlign: 'center'
        }}
      >
        {liveHint && (
          <Typography
            variant="caption"
            sx={{ color: 'text.secondary', fontStyle: 'italic' }}
          >
            {liveHint}
          </Typography>
        )}
      </Box>
      {/* Also always mounted, and heard rather than seen: the pane above
          already shows the question. */}
      <Box role="status" sx={visuallyHidden}>{liveQuestion}</Box>
    </Box>
  );
};
